import React, { useState, useMemo, useEffect } from 'react';
import { Menu, MenuItem, Box, Typography, Checkbox, TextField, InputAdornment, Button, Divider } from '@mui/material';
import { Search, PushPin, PushPinOutlined } from '@mui/icons-material';
import { MRT_TableInstance } from 'material-react-table';

interface CustomManageColumnsMenuProps<TData extends Record<string, unknown> = Record<string, unknown>> {
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  table: MRT_TableInstance<TData>;
  title?: string;
}

export function CustomManageColumnsMenu<TData extends Record<string, unknown> = Record<string, unknown>>({
  anchorEl,
  setAnchorEl,
  table,
  title = 'Manage Columns',
}: CustomManageColumnsMenuProps<TData>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const open = Boolean(anchorEl);

  // Store the initial states (only captured once when menu first opens)
  const [initialColumnVisibility, setInitialColumnVisibility] = useState<Record<string, boolean>>({});
  const [initialColumnPinning, setInitialColumnPinning] = useState<{ left?: string[]; right?: string[] }>({});
  const [hasCapturedInitialState, setHasCapturedInitialState] = useState(false);

  // Capture initial state only the first time the menu opens
  useEffect(() => {
    if (open && !hasCapturedInitialState) {
      setInitialColumnVisibility(table.getState().columnVisibility);
      setInitialColumnPinning(table.getState().columnPinning);
      setHasCapturedInitialState(true);
    }
  }, [open, table, hasCapturedInitialState]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
    setShowAll(false);
  };

  // Get all columns that can be hidden
  const hideableColumns = useMemo(() => {
    return table.getAllLeafColumns().filter(column => {
      // Check if column can be hidden by looking at the column definition
      const columnDef = column.columnDef;
      return columnDef.enableHiding !== false;
    });
  }, [table]);

  // Filter columns based on search query
  const filteredColumns = useMemo(() => {
    if (!searchQuery) return hideableColumns;
    return hideableColumns.filter(column => column.columnDef.header?.toString().toLowerCase().includes(searchQuery.toLowerCase()));
  }, [hideableColumns, searchQuery]);

  // Get current column visibility state
  const columnVisibility = table.getState().columnVisibility;

  // Handle individual column visibility toggle
  const handleColumnVisibilityChange = (columnId: string, isVisible: boolean) => {
    table.setColumnVisibility(prev => ({
      ...prev,
      [columnId]: isVisible,
    }));
  };

  // Handle show/hide all columns
  const handleShowHideAll = () => {
    const newShowAll = !showAll;
    setShowAll(newShowAll);

    const newVisibility: Record<string, boolean> = {};
    hideableColumns.forEach(column => {
      newVisibility[column.id] = newShowAll;
    });

    table.setColumnVisibility(newVisibility);
  };

  // Handle reset to initial state (both visibility and pinning)
  const handleReset = () => {
    // Reset column visibility to initial state
    table.setColumnVisibility(initialColumnVisibility);

    // Reset column pinning to initial state
    table.setColumnPinning(initialColumnPinning);

    setShowAll(false);
  };

  // Count visible and hidden columns (only for hideable columns)
  const visibleCount = hideableColumns.filter(column => columnVisibility[column.id] !== false).length;
  const hiddenCount = hideableColumns.length - visibleCount;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 300,
          maxHeight: 450,
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ position: 'relative', p: 2, pb: 1, pt: 0 }}>
        <Typography variant='h6' fontWeight='bold'>
          {title}
        </Typography>
      </Box>

      {/* Search Bar - Always Visible */}
      <Box sx={{ px: 2, pb: 0 }}>
        <TextField
          fullWidth
          size='small'
          placeholder='Search'
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f5f5f5',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'transparent',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'transparent',
              },
            },
          }}
        />
      </Box>

      <Divider />

      {/* Column List - Scrollable Section */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
          {filteredColumns.map(column => {
            const isVisible = columnVisibility[column.id] !== false;
            const headerText = column.columnDef.header?.toString() || column.id;

            // Get current pinning state
            const columnPinning = table.getState().columnPinning;
            const isPinnedLeft = columnPinning?.left?.includes(column.id);
            const isPinnedRight = columnPinning?.right?.includes(column.id);
            const isPinned = isPinnedLeft || isPinnedRight;

            return (
              <MenuItem
                key={column.id}
                dense
                sx={{
                  px: 2,
                  py: 0.5, // Reduced vertical padding
                  fontSize: '0.875rem', // Smaller font size
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                {/* Visibility Checkbox */}
                <Checkbox
                  checked={isVisible}
                  size='small'
                  onClick={e => {
                    e.stopPropagation();
                    handleColumnVisibilityChange(column.id, !isVisible);
                  }}
                  sx={{
                    padding: '2px',
                    color: '#1976d2',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                  }}
                />

                {/* Column Name */}
                <Typography
                  variant='body2'
                  sx={{
                    flex: 1,
                    fontSize: '0.875rem',
                    lineHeight: 1.2, // Tighter line height
                    color: isVisible ? 'text.primary' : 'text.secondary',
                    ml: 1, // Add margin to create space after checkbox
                  }}
                >
                  {headerText}
                </Typography>

                {/* Pinning Controls */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {/* Left Pin Button - Pins column to the left side */}
                  <Button
                    size='small'
                    variant='text'
                    onClick={e => {
                      e.stopPropagation();
                      const currentPinning = table.getState().columnPinning;

                      // If already pinned left, remove it
                      if (isPinnedLeft) {
                        const newLeftPins = (currentPinning?.left || []).filter(id => id !== column.id);
                        table.setColumnPinning({
                          ...currentPinning,
                          left: newLeftPins,
                        });
                      } else {
                        // Pin to left, remove from right if it was pinned there
                        const newLeftPins = [...(currentPinning?.left || []), column.id];
                        const newRightPins = (currentPinning?.right || []).filter(id => id !== column.id);

                        table.setColumnPinning({
                          ...currentPinning,
                          left: newLeftPins,
                          right: newRightPins,
                        });
                      }

                      // If column was hidden, make it visible when pinning
                      if (!isVisible) {
                        handleColumnVisibilityChange(column.id, true);
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      p: 0.25, // Reduced padding
                      ml: -0.5, // Pull slightly left
                      color: isPinnedLeft ? '#1976d2' : 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      },
                    }}
                  >
                    {isPinnedLeft ? <PushPin fontSize='small' /> : <PushPinOutlined fontSize='small' />}
                  </Button>

                  {/* Right Pin Button - Pins column to the left of actions column */}
                  <Button
                    size='small'
                    variant='text'
                    onClick={e => {
                      e.stopPropagation();
                      const currentPinning = table.getState().columnPinning;

                      // If already pinned right, remove it
                      if (isPinnedRight) {
                        const newRightPins = (currentPinning?.right || []).filter(id => id !== column.id);
                        table.setColumnPinning({
                          ...currentPinning,
                          right: newRightPins,
                        });
                      } else {
                        // Pin to right (before actions), remove from left if it was pinned there
                        // Insert the column at the beginning of right pins so it appears before actions
                        const newRightPins = [column.id, ...(currentPinning?.right || [])];
                        const newLeftPins = (currentPinning?.left || []).filter(id => id !== column.id);

                        table.setColumnPinning({
                          ...currentPinning,
                          left: newLeftPins,
                          right: newRightPins,
                        });
                      }

                      // If column was hidden, make it visible when pinning
                      if (!isVisible) {
                        handleColumnVisibilityChange(column.id, true);
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      p: 0.25, // Reduced padding
                      ml: -0.5, // Pull slightly left
                      color: isPinnedRight ? '#1976d2' : 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      },
                    }}
                  >
                    {isPinnedRight ? <PushPin fontSize='small' /> : <PushPinOutlined fontSize='small' />}
                  </Button>
                </Box>
              </MenuItem>
            );
          })}
        </Box>
      </Box>

      <Divider />

      {/* Action Buttons - Always Visible */}
      <Box sx={{ p: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button size='small' onClick={handleShowHideAll} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          {showAll ? 'Hide All' : 'Show All'}
        </Button>
        <Button size='small' onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Reset
        </Button>
      </Box>

      {/* Column Count - Always Visible */}
      <Box sx={{ px: 0, pb: 0, textAlign: 'center' }}>
        <Typography variant='caption' color='text.secondary'>
          {visibleCount} visible, {hiddenCount} hidden
        </Typography>
      </Box>
    </Menu>
  );
}
